import {  type IBracketStore, } from "@store";
import type { AmountString, BrktTransaction, TokenDefinition, } from "@bracket/types";
import { produce } from 'immer';
import type { StateCreator, } from "zustand";
import type { Store } from '../useStore';
import type{ ISwapSlice, SwapMode, } from "./types";
import { SwapSliceDefaultprops, } from "./defaults";
import { getChainId, switchChain, watchAccount, } from "wagmi/actions";
import { wagmiConfig as config, } from "@wagmiconfig";
import * as R from "./resolvers";
import { awaitTruthy, } from "@store/helpers";
import { convertToUSD, trackBrktEvent, } from "@utils";
import { formatDollars, } from "@formatters";
import * as H from "./helpers";
import { getTokenDefinition, getTokens, } from "@bracket/chainography";



export const createSwapSlice: StateCreator<IBracketStore, [], [], ISwapSlice> = ( set, get, ) => {


	const fetch = async () => {

		const { useStore, } = await import("@store");
		await awaitTruthy(useStore, state => state.wallet.ready);

		const { ready, fetching, tokenIn, tokenOut, mode, } = get().swap;

		if(!ready && !fetching) {

			if(getChainId(config) !== 1) {
				await switchChain(config, { chainId: 1, });
			}
			set(produce<Store>(state => {
				state.swap.fetching = true;
			}));

			const [
				inputBalanceBi,
				inputAllowanceBi,
				outputBalanceBi,
				outputMaximum,
			] = await Promise.all([
				R.getTokenBalance({ token: tokenIn, }),
				R.getTokenAllowance({ token: tokenIn, }),
				R.getTokenBalance({ token: tokenOut, }),
				R.getCollateralMaximum({ token: mode === "BUY" ? tokenIn : tokenOut, }),
			]);

			await awaitTruthy(useStore, state => state.platform.ready);

			const redeemableTokens = (
				[...get().platform.colateralBalances]
					.sort((a,b) => b.count - a.count)
					.map(t => ({ ...getTokenDefinition({ symbol: t.symbol, chainId: 1, }), count: t.count, }))
			);

			set(produce<Store>(state => {
				state.swap.inputBalance = H.toWeiString(inputBalanceBi, tokenIn);
				state.swap.inputHasBalance = H.amountToBigInt("0", tokenIn) <= inputBalanceBi;
				state.swap.inputAllowance = inputAllowanceBi;
				state.swap.outputBalance = H.toWeiString(outputBalanceBi, tokenOut);
				state.swap.outputMaximum = outputMaximum;
				state.swap.outputAvailable = outputMaximum <= outputBalanceBi;
				state.swap.redeemableTokens = redeemableTokens;
				state.swap.preferredRedeemToken = redeemableTokens[0];
				if(state.swap.mode === "REDEEM" && redeemableTokens.length > 0) {
					state.swap.tokensOut = redeemableTokens;
					state.swap.tokenOut = redeemableTokens[0];
				}
				state.swap.ready = true;
				state.swap.fetching = false;
			}));

			watchAccount(config, {
				onChange: (next, prev) => {
					reset();
				},
			});
		}

		set(produce<Store>(state => {
			state.swap.ready = true;
			state.swap.fetching = false;
		}));
	}


	const onTokenInChange = (t: TokenDefinition) => {

		const { amountIn, } = get().swap;

		set(produce<Store>(state => {
			state.swap.tokenIn = t;
			state.swap.native = false; //R.isNativeToken(t);
		}));

		(async () => {

			const [
				inputBalanceBi,
				outputMaximum,
			] = await Promise.all([
				R.getTokenBalance({ token: t, }),
				R.getCollateralMaximum({ token: t, }),
			]);

			set(produce<Store>(state => {
				state.swap.inputBalance = H.toWeiString(inputBalanceBi, t);
				state.swap.outputMaximum = outputMaximum;
			}));

			onTokenInAmountChange(amountIn);

		})();

	}


	const onTokenOutChange = (t: TokenDefinition) => {

		const {
			amountIn,
		} = get().swap;

		set(produce<Store>(state => { state.swap.tokenOut = t; }));

		(async () => {

			const [
				outputBalanceBi,
				outputMaximum,
			] = await Promise.all([
				R.getTokenBalance({ token: t, }),
				R.getCollateralMaximum({ token: t, }),
			]);

			set(produce<Store>(state => {
				state.swap.outputBalance = H.toWeiString(outputBalanceBi, t);
				state.swap.outputMaximum = outputMaximum;
			}));

			onTokenInAmountChange(amountIn);

		})();

	}


	const onTokenInAmountChange = (v: AmountString) => {

		if(v.length > 0 && (!/^[0-9.]+$/.test(v) || v.startsWith(".") || v.includes("..")) ) {
			return;
		}

		set(produce<Store>(state => {
			state.swap.amountIn = v;
		}));
		
		const {
			mode,
			tokenIn,
			tokenOut,
		} = get().swap;
		
		(async () => {

			let errored = false;

			set(produce<Store>(state => {
				state.swap.quoting = true;
			}));

			const [
				quote,
				allowance,
				routerAllowance,
				balance,
				swapValueUSD,
			] = await Promise.all([
				R.getQuote({ mode, amount: v, tokenIn, tokenOut, }),
				R.getTokenAllowance({ token: tokenIn, }),
				R.getContractAllowance({ token: tokenIn, spender: "BrktETHRouter", }),
				R.getTokenBalance({ token: tokenIn, }),
				convertToUSD({ amount: v, token: tokenIn, }),
			]).catch( err => {
				get().notificationSlice.addNotification(N.invalidQuoteNotification());
				errored = true;
				return [];
			});

			if(errored) {
				reset();
				return;
			}

			if(!quote.valid) {
				return;
			}

			set(produce<Store>(state => {
				state.swap.amountOut = H.toWeiString(quote.amount, tokenOut);
				state.swap.swapValueUSD = formatDollars(swapValueUSD);
				state.swap.inputApproved = tokenIn.symbol === "ETH"
																		? true
																		: tokenIn.symbol === "WETH"
																			? H.amountToBigInt(v, tokenIn) <= routerAllowance
																			: H.amountToBigInt(v, tokenIn) <= allowance;
				state.swap.inputHasBalance = H.amountToBigInt(v, tokenIn) <= balance;
				state.swap.native = quote.native;
				state.swap.quotedCallResult = quote.native ? quote.calldata : null;
				state.swap.impact = quote.native ? quote.priceImpact : null;
				state.swap.gas = quote.native ? quote.gas : 0;
				state.swap.quoting = false;
				state.swap.evaluation = R.evaluateSwap(state.swap);
			}));
		
		})();

	}


	const onTokenOutAmountChange = (v: AmountString) => {

		if(v.length > 0 && !/^[0-9.]+$/.test(v)) {
			return;
		}

		const {
			mode,
		} = get().swap;

		switch(mode) {

			case "BUY":
				set(produce<Store>(state => {
					state.swap.amountOut = v;
				}));
				(async () => {
					// side effects for BUY mode
				})();
				break;

			case "REDEEM":
				set(produce<Store>(state => {
					// state.swap.amountIn = v;
				}));
				(async () => {
					// side effects for REDEEM mode
				})();
				break;

			default:
				throw new Error(`Invalid mode: ${mode}`);
		}
	}


	const setInputMax = async () => {

		const {
			mode,
			inputBalance,
		 } = get().swap;

		 switch(mode) {	

			case "REDEEM": {
				onTokenInAmountChange(await R.enforceMaximum(get));
				return;
			}

			case "BUY": {
				onTokenInAmountChange(inputBalance);
				return;
			}

			default: {
				console.warn("setInputMax invoked with unknown mode", mode);
				return;
			}

		}
	
	}


	const setMode = (m: SwapMode) => {

		// switch to the argued mode, setting tokenIn and tokenOut to initial values for the mode
		switch(m) {

			case "BUY":

				set(produce<Store>(state => {
					state.swap.mode = m;
					state.swap.tokenIn = getTokenDefinition({ symbol: "WSTETH", chainId: 1, });
					state.swap.tokenOut = getTokenDefinition({ symbol: "BRKTETH", chainId: 1, });
					state.swap.tokensIn = getTokens({ platform_supply : true, withETH: true, withWETH: true, });
					state.swap.tokensOut = [getTokenDefinition({ symbol: "BRKTETH", chainId: 1, })];
					state.swap.amountIn = "0";
					state.swap.amountOut = "0";
					state.swap.swapValueUSD = "$0.00";
					state.swap.inputBalance = "0";
					state.swap.outputBalance = "0";
					state.swap.inputAllowance = BigInt(0);
					state.swap.outputAllowance = BigInt(0);
					state.swap.native = false;
					state.swap.evaluation = "OK";
				}));

				(async () => {
					// apply BUYmode specific side-effects
					// await get().swap.fetch();
				})();
				break;

			case "REDEEM": {

				const {
					preferredRedeemToken,
					redeemableTokens,
				} = get().swap;

				set(produce<Store>(state => {
					state.swap.mode = m;
					state.swap.tokenIn = getTokenDefinition({ symbol: "BRKTETH", chainId: 1, });
					state.swap.tokenOut = preferredRedeemToken;
					state.swap.tokensIn = [getTokenDefinition({ symbol: "BRKTETH", chainId: 1, })];
					state.swap.tokensOut = redeemableTokens;
					state.swap.amountIn = "0";
					state.swap.amountOut = "0";
					state.swap.swapValueUSD = "$0.00";
					state.swap.inputBalance = "0";
					state.swap.outputBalance = "0";
					state.swap.inputAllowance = BigInt(0);
					state.swap.outputAllowance = BigInt(0);
					state.swap.native = false;
					state.swap.evaluation = "OK";
				}));

				(async () => {


				})();

				break;
			}

			default:
				throw new Error(`Invalid mode: ${m}`);

		}


		// provide side-effect for the mode change
		(async () => {

			const {
				mode,
				tokenIn,
				tokenOut,
			} = get().swap;

			const [
				inputBalanceBi,
				inputAllowanceBi,
				outputBalanceBi,
				outputMaximum,
			] = await Promise.all([
				R.getTokenBalance({ token: tokenIn, }),
				R.getTokenAllowance({ token: tokenIn, }),
				R.getTokenBalance({ token: tokenOut, }),
				R.getCollateralMaximum({ token: mode === "BUY" ? tokenIn : tokenOut, }),
			]);

			set(produce<Store>(state => {
				state.swap.inputBalance = H.toWeiString(inputBalanceBi, tokenIn);
				state.swap.inputAllowance = inputAllowanceBi;
				state.swap.outputBalance = H.toWeiString(outputBalanceBi, tokenOut);
				state.swap.outputMaximum = outputMaximum;
				state.swap.outputAvailable = outputMaximum <= outputBalanceBi;
			}));

		})();

		return;

	};


	const toggleMode = () => {
		const { mode, } = get().swap;
		setMode(mode === "BUY" ? "REDEEM" : "BUY");
	};


	const approveBrktBuy = () => {

		set(produce<Store>(state => {
			state.swap.transacting = true;
		}));

		const {
			tokenIn,
			amountIn,
		} = get().swap;

		(async () => {
			try {
				const approved = await R.approveTokenSpend({
					token: tokenIn,
					amount: H.amountToBigInt(amountIn, tokenIn),
					spender: tokenIn.symbol === "WETH" ? "BrktETHRouter" : "BRKTETH",
				});
				set(produce<Store>(state => {
					state.swap.transacting = false;
					state.swap.inputApproved = approved;
				}));
			} catch (err) {
				set(produce<Store>(state => {
					state.swap.transacting = false;
				}));
			}
		})();

	};


	const swap = () => {

		set(produce<Store>(state => {
			state.swap.transacting = true;
		}));

		const {
			tokenIn,
			amountIn,
			amountOut,
		} = get().swap;

		const {
			addNotification,
		} = get().notificationSlice;

		(async () => {

			const {
				hash,
				error,
			} = await R.buy({
				tokenIn,
				amountIn,
				amountOut,
			});

			// failure
			if(error) {
				addNotification(N.mintFailureNotification(tokenIn, amountIn, error));
				set(produce<Store>(state => {
					state.swap.transacting = false;
				}));
				return;
			}

			// success
			if(hash) {
				addNotification(N.mintSuccessNotification(tokenIn, amountIn, hash));
				set(produce<Store>(state => {
					state.swap.transacting = false;
				}));
				await trackBrktEvent({
					type: "buy",
					value: Number.parseFloat(amountOut),
					tokenRedeemed: tokenIn.symbol,
				});
				reset();
				return;
			}

			// exception
			addNotification(N.mintFailureNotification(tokenIn, amountIn, new Error("reached an exception")));
			set(produce<Store>(state => {
				state.swap.transacting = false;
			}));

		})();
	};


	const redeem = () => {

		set(produce<Store>(state => {
			state.swap.transacting = true;
		}));

		const {
			tokenOut,
			amountIn,
			amountOut,
		} = get().swap;

		const {
			addNotification,
		} = get().notificationSlice;

		(async () => {
			const { hash, error } = await R.redeem({
				token: tokenOut,
				amount: amountOut,
				brkt: amountIn,
			});

			if(error) {
				addNotification(N.redeemFailureNotification(tokenOut, amountOut, error));
				set(produce<Store>(state => {
					state.swap.transacting = false;
				}));
				return;
			}

			// success
			if(hash) {
				addNotification(N.redeemSuccessNotification(tokenOut, amountOut, hash));
				set(produce<Store>(state => {
					state.swap.transacting = false;
				}));
				await trackBrktEvent({
					type: "redeem_req",
					value: Number.parseFloat(amountOut),
					tokenRedeemed: tokenOut.symbol,
				});
				reset();
				return;
			}

			// exception
			addNotification(N.redeemFailureNotification(tokenOut, amountOut, new Error("reached an exception")));
			set(produce<Store>(state => {
					state.swap.transacting = false;
			}));


		})();

	};


	const claim = ( txn: BrktTransaction ) => {

		set(produce<Store>(state => {
			state.swap.transacting = true;
		}));


		const {
			addNotification,
		} = get().notificationSlice;

		(async () => {

			const {
				hash,
				error,
			} = await R.claim({ txn, });

			if(hash) {
				addNotification(N.claimSuccessNotification(txn, hash));
				await trackBrktEvent({
					type: "redeem_claim",
					value: Number.parseFloat(txn.amount),
					tokenRedeemed: getTokenDefinition({ address: txn.token, chainId: 1, }).symbol,
				});
				set(produce<Store>(state => {
					state.swap.transacting = false;
				}));
				return;
			}

			if(error) {
				addNotification(N.claimFailureNotification(txn, error));
				set(produce<Store>(state => {
					state.swap.transacting = false;
				}));
				return;
			}

			throw new Error("swap.resolvers.claim: reached an exception");

		})();

	};


	const reset = () => {
		set(produce<Store>(state => {
			state.swap.mode = "REDEEM";
			state.swap.amountIn = "0";
			state.swap.amountOut = "0";
			state.swap.swapValueUSD = "$0.00";
			state.swap.tokenIn = getTokenDefinition({ symbol: "BRKTETH", chainId: 1, });
			state.swap.tokensIn = [getTokenDefinition({ symbol: "BRKTETH", chainId: 1, }),];
			state.swap.tokenOut = getTokenDefinition({ symbol: "RSETH", chainId: 1, });
			state.swap.tokensOut = [];
			state.swap.native = false;
			state.swap.ready = false;
			state.swap.impact = null;
			state.swap.gas = null;
			state.swap.transacting = false;
			state.swap.quoting = false;
			state.swap.quoted = false;
			state.swap.quotedCallResult = null;
			state.swap.evaluation = "OK";
		}));

		( async () => {
			const { useStore, } = await import("@store");
			await awaitTruthy(useStore, state => state.wallet.ready);
			fetch();
		} )();

	}


	const resetAsync = async () => {
		reset();
	};

	// place at the bottom of the call stack.
	setTimeout(_ => fetch(), 0);

	return {

		...SwapSliceDefaultprops,
		ready: false,
		fetching: false,

		fetch,
		onTokenInChange,
		onTokenOutChange,
		onTokenInAmountChange,
		onTokenOutAmountChange,
		setInputMax,
		setMode,
		toggleMode,
		approveBrktBuy,
		swap,
		redeem,
		claim,
		reset,
		resetAsync,
	};

};
